import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import api from "@/services/api";
import DashboardLayout from "@/components/layout/DashboardLayout";
import PageHeader from "@/components/common/PageHeader";
import InlineDocViewer from "@/components/common/InlineDocViewer";
import ExamResultDetail from "@/components/common/ExamResultDetail";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { ArrowLeft, CheckCircle, FileText, Download, XCircle } from "lucide-react";

const ReviewApplication = () => {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();

  const [selected, setSelected] = useState<any>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [examAssignments, setExamAssignments] = useState<any[]>([]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState<"approved" | "rejected" | null>(null);

  const backToQueue = () => navigate("/aca/review");

  useEffect(() => {
    if (!appId) return;
    (async () => {
      const { data: app } = await supabase.from("applications").select("*").eq("id", appId).maybeSingle();
      setSelected(app);
      if (app) {
        const { data: docs } = await supabase.from("documents").select("*").eq("application_id", app.id);
        setDocuments(docs || []);
        try {
          const res = await api.get(`/think-exam/results/${app.id}`);
          setExamAssignments(res.data?.assignments || []);
        } catch (_) {}
      }
      setLoading(false);
    })();
  }, [appId]);

  const updateStatus = async (newStatus: string) => {
    if (!selected) return;
    setActionLoading(true);
    try {
      await api.patch(`/applications/${selected.id}/status`, { status: newStatus, notes });
      toast({ title: newStatus === "approved" ? "Application Approved" : "Application Rejected" });
      backToQueue();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
      setActionLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <PageHeader title="Review Application" crumbLabel="Review Queue" crumbTo="/aca/review" />

        <button
          onClick={backToQueue}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group"
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to Review Queue
        </button>

        {loading ? (
          <div className="flex justify-center py-24">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : !selected ? (
          <Card className="glass-premium shadow-sm">
            <CardContent className="py-16 text-center text-muted-foreground">Application not found.</CardContent>
          </Card>
        ) : (
          <>
            {/* Header */}
            <Card className="glass-premium shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl gradient-warm flex items-center justify-center text-lg font-bold text-white shadow-md shrink-0">
                    {selected.full_name?.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2) || "?"}
                  </div>
                  <div>
                    <p className="text-foreground text-xl font-bold leading-tight">{selected.full_name}</p>
                    <p className="text-muted-foreground text-xs mt-0.5">
                      App ID: <span className="font-mono">{selected.id.slice(0, 8)}</span>
                      <span className="mx-2">·</span>
                      Submitted {format(new Date(selected.created_at), "MMM d, yyyy")}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid gap-6 lg:grid-cols-3 items-start">
              {/* Left column */}
              <div className="lg:col-span-2 flex flex-col gap-6">
                {/* Applicant details */}
                <Card className="glass-premium shadow-sm">
                  <CardContent className="p-6">
                    <h3 className="text-sm font-bold text-foreground border-l-[3px] border-primary pl-3 mb-4">Applicant Details</h3>
                    <div className="grid grid-cols-2 gap-0">
                      {[
                        { label: "Name", value: selected.full_name },
                        { label: "Email", value: selected.email },
                        { label: "Phone", value: selected.phone || "—" },
                        { label: "Qualification", value: selected.qualification || "—" },
                      ].map(({ label, value }, i) => (
                        <div key={label} className={`py-4 ${i % 2 === 0 ? "pr-8 border-r border-border/30" : "pl-8"} ${i < 2 ? "border-b border-border/30" : ""}`}>
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
                          <p className="text-sm font-semibold text-foreground break-words">{value}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Documents */}
                <Card className="glass-premium shadow-sm">
                  <CardContent className="p-6">
                    <h3 className="text-sm font-bold text-foreground border-l-[3px] border-primary pl-3 mb-4">Uploaded Documents</h3>
                    {documents.length > 0 ? (
                      <div className="space-y-2">
                        {documents.map((doc) => (
                          <div key={doc.id} className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-muted/10">
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                              <FileText className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-foreground">{doc.document_type}</p>
                              <p className="text-xs text-muted-foreground truncate">{doc.file_name}</p>
                            </div>
                            <div className="flex gap-1 shrink-0">
                              <InlineDocViewer filePath={doc.file_path} fileName={doc.file_name} />
                              <Button
                                size="sm" variant="outline" title="Download"
                                className="h-8 w-8 p-0 border-border/60 hover:bg-primary hover:text-white hover:border-primary transition-all duration-200"
                                onClick={async () => {
                                  const { data } = await supabase.storage
                                    .from("application-documents")
                                    .createSignedUrl(doc.file_path, 3600, { download: true });
                                  if (data?.signedUrl) {
                                    const a = document.createElement("a");
                                    a.href = data.signedUrl;
                                    a.download = doc.file_name;
                                    a.click();
                                  }
                                }}
                              >
                                <Download className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center py-6 text-center border rounded-xl bg-muted/10">
                        <FileText className="h-6 w-6 text-muted-foreground/30 mb-1" />
                        <p className="text-xs text-muted-foreground">No documents uploaded yet</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Exam Results */}
                {examAssignments.length > 0 && (
                  <Card className="glass-premium shadow-sm">
                    <CardContent className="p-6">
                      <h3 className="text-sm font-bold text-foreground border-l-[3px] border-primary pl-3 mb-4">Exam Results</h3>
                      <div className="space-y-3">
                        {examAssignments.map((ea: any) => (
                          <div key={ea.id} className={`rounded-xl border p-4 ${ea.status === "passed" ? "border-green-200 bg-green-50/50 dark:bg-green-950/10" : ea.status === "failed" ? "border-red-200 bg-red-50/50 dark:bg-red-950/10" : "border-border/50 bg-muted/10"}`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-foreground truncate">{ea.think_exam_test_name || ea.think_exam_test_id}</p>
                                <p className="text-xs text-muted-foreground">{ea.think_exam_group_name}</p>
                                {ea.score != null && (
                                  <p className="text-xs mt-1 font-medium">Score: {ea.score}/{ea.total_marks} ({ea.percentage}%)</p>
                                )}
                              </div>
                              <span className={`shrink-0 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${ea.status === "passed" ? "bg-green-100 text-green-700" : ea.status === "failed" ? "bg-red-100 text-red-700" : "bg-muted text-muted-foreground"}`}>
                                {ea.status === "passed" ? "Passed" : ea.status === "failed" ? "Failed" : "Pending"}
                              </span>
                            </div>
                            {(ea.status === "passed" || ea.status === "failed") && <ExamResultDetail exam={ea} />}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Right column — notes + actions */}
              <div className="flex flex-col gap-6">
                {(selected.reviewer_notes || selected.notes) && (
                  <Card className="glass-premium shadow-sm">
                    <CardContent className="p-6">
                      <h3 className="text-sm font-bold text-foreground border-l-[3px] border-primary pl-3 mb-3">Notes</h3>
                      {selected.reviewer_notes && (
                        <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/20 border-l-[3px] border-amber-400 mb-2">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-600 mb-1">GrowLearn Notes</p>
                          <p className="text-sm text-foreground">{selected.reviewer_notes}</p>
                        </div>
                      )}
                      {selected.notes && (
                        <div className="p-3 rounded-xl bg-muted/30 border border-border/40">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Application Notes</p>
                          <p className="text-sm text-foreground">{selected.notes}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                <Card className="glass-premium shadow-sm">
                  <CardContent className="p-6">
                    <h3 className="text-sm font-bold text-foreground border-l-[3px] border-primary pl-3 mb-4">ACA Review Notes</h3>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add review notes..."
                      rows={4}
                      className="resize-none"
                    />
                    <div className="flex flex-col gap-3 mt-5">
                      <Button
                        disabled={actionLoading}
                        onClick={() => setConfirmAction("approved")}
                        className="h-11 gradient-warm text-white border-0 shadow-md hover:opacity-90 transition-opacity"
                      >
                        <CheckCircle className="mr-2 h-4 w-4" />
                        {actionLoading ? "Processing..." : "Approve"}
                      </Button>
                      <Button
                        variant="outline"
                        disabled={actionLoading}
                        onClick={() => setConfirmAction("rejected")}
                        className="h-11 border-destructive/40 text-destructive hover:bg-destructive hover:text-white hover:border-destructive transition-all"
                      >
                        <XCircle className="mr-2 h-4 w-4" />
                        Reject
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </>
        )}
      </div>

      <AlertDialog open={confirmAction !== null} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction === "approved" ? "Approve this application?" : "Reject this application?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to {confirmAction === "approved" ? "approve" : "reject"} the application from{" "}
              <span className="font-semibold text-foreground">{selected?.full_name || "this applicant"}</span>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmAction && updateStatus(confirmAction)}
              className={confirmAction === "approved" ? "gradient-warm text-white border-0" : "bg-destructive text-white hover:bg-destructive/90"}
            >
              {confirmAction === "approved" ? "Yes, approve" : "Yes, reject"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default ReviewApplication;
